import { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Progress,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Tooltip,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { TimeIcon, WarningIcon } from '@chakra-ui/icons'
import { PRM_ProfilerResults, PRM_Error, PRM_Benchmark, PRM_Benchmark_Contest } from '../types/unified_models'

interface ProfilerModuleProps {
  profiler: PRM_ProfilerResults | PRM_Error | null
  benchmark?: PRM_Benchmark | PRM_Benchmark_Contest | PRM_Error | null
}

export default function ProfilerModule({ profiler, benchmark }: ProfilerModuleProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')
  
  const [sortBy, setSortBy] = useState<'line' | 'exclusive' | 'inclusive'>('exclusive')
  const [showAllLines, setShowAllLines] = useState(false)

  if (!profiler) {
    return (
      <VStack align="stretch" spacing={3} w="100%" maxW="100%">
        <HStack>
          <Icon as={TimeIcon} color="gray.400" />
          <Heading size="sm" color={textColor}>Performance Profiler</Heading>
        </HStack>
        <Alert status="info">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm">
              Enable the Performance Profiler option before submission to see detailed line-by-line performance data.
            </Text>
            <Text fontSize="xs" color="gray.600">
              Note: When a specific input is selected for profiling, all tests (correctness, benchmark, profiler) will run only on that input.
            </Text>
          </VStack>
        </Alert>
      </VStack>
    )
  }

  // Check if it's an error response
  if ('error' in profiler && profiler.error) {
    return (
      <VStack align="stretch" spacing={3} w="100%" maxW="100%">
        <HStack>
          <Icon as={WarningIcon} color="red.400" />
          <Heading size="sm" color={textColor}>Performance Profiler</Heading>
        </HStack>
        <Text fontWeight="bold" color="red.500" fontSize="sm">
          Failed to generate profiler results.
        </Text>
        {profiler.stdout && (
          <Box>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Out:</Text>
            <Box
              as="pre"
              bg="gray.100"
              p={2}
              borderRadius="md"
              fontSize="xs"
              overflowX="auto"
              maxH="200px"
            >
              {profiler.stdout}
            </Box>
          </Box>
        )}
        {profiler.stderr && (
          <Box>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>Standard Error:</Text>
            <Box
              as="pre"
              bg="gray.100"
              p={2}
              borderRadius="md"
              fontSize="xs"
              overflowX="auto"
              maxH="200px"
            >
              {profiler.stderr}
            </Box>
          </Box>
        )}
      </VStack>
    )
  }

  const profilerData = profiler as PRM_ProfilerResults
  
  // Calculate runtime ratio (function time vs total time including setup)
  const getRuntimeRatio = (): { ratio: number, hasData: boolean } => {
    if (!benchmark || 'error' in benchmark) {
      return { ratio: 0, hasData: false }
    }
    
    // Handle both PRM_Benchmark and PRM_Benchmark_Contest
    let submissionResults: any = null
    if ('submission' in benchmark) {
      submissionResults = benchmark.submission
    }
    
    if (!submissionResults) {
      return { ratio: 0, hasData: false }
    }
    
    // Handle both single result and array of results
    const result = Array.isArray(submissionResults) ? submissionResults[0] : submissionResults
    
    if (result && result.runtime && result.runtime_including_overhead) {
      const ratio = result.runtime / result.runtime_including_overhead
      return { ratio, hasData: true }
    }
    
    return { ratio: 0, hasData: false }
  }
  
  const { ratio: runtimeRatio, hasData: hasRuntimeData } = getRuntimeRatio()
  
  // Calculate original coverage percentages (including setup overhead)
  const originalCoveragePercent = (profilerData.accountability.total > 0)
    ? ((profilerData.accountability.line_attributed + profilerData.accountability.block_attributed) / profilerData.accountability.total) * 100
    : 0
  
  const originalLineOnlyPercent = (profilerData.accountability.total > 0)
    ? (profilerData.accountability.line_attributed / profilerData.accountability.total) * 100
    : 0
  
  // Calculate block-only percentage (blocks that aren't already counted in lines)
  const originalBlockOnlyPercent = (profilerData.accountability.total > 0)
    ? (profilerData.accountability.block_attributed / profilerData.accountability.total) * 100
    : 0
  

  
  // Calculate adjusted coverage percentages (scaled by runtime ratio to exclude setup overhead)
  const adjustedCoveragePercent = hasRuntimeData && runtimeRatio > 0 
    ? Math.min(originalCoveragePercent / runtimeRatio, 100)
    : originalCoveragePercent
    
  const adjustedLineOnlyPercent = hasRuntimeData && runtimeRatio > 0
    ? Math.min(originalLineOnlyPercent / runtimeRatio, 100) 
    : originalLineOnlyPercent
    
  const adjustedBlockOnlyPercent = hasRuntimeData && runtimeRatio > 0
    ? Math.min(originalBlockOnlyPercent / runtimeRatio, 100) 
    : originalBlockOnlyPercent
  
  // Use adjusted percentages for display and color scheme
  const coveragePercent = adjustedCoveragePercent
  


  // Helper function to scale percentages by runtime ratio
  const scalePercentage = (originalPercent: number): number => {
    if (!hasRuntimeData || runtimeRatio <= 0) return originalPercent
    return Math.min(originalPercent / runtimeRatio, 100)
  }

  // Helper function to get color intensity based on percentage
  const getHeatmapColor = (percentage: number): string => {
    if (percentage > 10) return 'red'
    if (percentage > 5) return 'orange'
    if (percentage > 2) return 'yellow'
    if (percentage > 1) return 'blue'
    return 'gray'
  }

  // Sort lines based on current criteria
  const sortedLines = [...profilerData.lines].sort((a, b) => {
    switch (sortBy) {
      case 'line':
        return a.line_number - b.line_number
      case 'exclusive':
        return b.percentage_exclusive - a.percentage_exclusive
      case 'inclusive':
        return b.percentage_inclusive - a.percentage_inclusive
      default:
        return a.line_number - b.line_number
    }
  })

  // Filter lines to show only significant ones by default (using scaled percentages)
  const filteredLines = showAllLines 
    ? sortedLines 
    : sortedLines.filter(line => 
        scalePercentage(line.percentage_exclusive) > 0.1 || 
        scalePercentage(line.percentage_inclusive) > 0.1
      )

  // Calculate top hotspots (using scaled percentages for filtering and sorting)
  const topHotspots = sortedLines
    .filter(line => scalePercentage(line.percentage_exclusive) > 1)
    .slice(0, 5)

  return (
    <VStack align="stretch" spacing={4} w="100%" maxW="100%" minW={0} overflow="hidden">
      <HStack>
        <Icon as={TimeIcon} color="purple.400" />
        <Heading size="sm" color={textColor}>Performance Profiler</Heading>
      </HStack>

      {/* Profile Data Summary */}
      <Box p={3} borderWidth={1} borderColor={borderColor} borderRadius="md" bg={useColorModeValue('blue.50','blue.900')}>
        <Text fontSize="sm" color={textColor} fontWeight="medium">
          📊 Profile Data Summary
        </Text>
        <Text fontSize="xs" color={mutedColor} mt={1}>
          Approximately <strong>{coveragePercent.toFixed(1)}%</strong> of profiling samples were attributed to your code, 
          with <strong>{adjustedLineOnlyPercent.toFixed(1)}%</strong> attributed to specific source lines 
          and <strong>{adjustedBlockOnlyPercent.toFixed(1)}%</strong> associated with functions. 
          The remaining <strong>{(100 - coveragePercent).toFixed(1)}%</strong> may contribute to benchmark runtime, but were not attributed to your code.
          {hasRuntimeData && runtimeRatio < 1 && (
            <> Percentages have been adjusted to exclude untimed setup overhead—see the "Unattributed" tab for more details.</>
          )}
          {(!hasRuntimeData || runtimeRatio >= 1) && (
            <> See the "Unattributed" tab for additional profiling details.</>
          )}
        </Text>
      </Box>

      {/* Top Hotspots */}
      {topHotspots.length > 0 && (
        <Box>
          <Text fontWeight="medium" fontSize="sm" color={textColor} mb={2}>
            🔥 Performance Hotspots (&gt;1% exclusive time)
          </Text>
          <VStack spacing={2} align="stretch">
            {topHotspots.map((line, index) => {
              const scaledPercent = scalePercentage(line.percentage_exclusive)
              
              return (
                <Box
                  key={line.line_number}
                  p={3}
                  borderWidth={1}
                  borderColor={borderColor}
                  borderRadius="md"
                  bg={bgColor}
                >
                  <HStack justify="space-between">
                    <HStack>
                      <Badge colorScheme="red" fontSize="xs">#{index + 1}</Badge>
                      <Text fontSize="sm" fontWeight="medium">Line {line.line_number}</Text>
                      <Badge colorScheme={getHeatmapColor(scaledPercent) as any} fontSize="xs">
                        {scaledPercent.toFixed(2)}%
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color={mutedColor}>
                      {line.samples_exclusive.toLocaleString()} samples
                    </Text>
                  </HStack>
                  <Code fontSize="xs" mt={2} display="block" p={2} bg="gray.50">
                    {line.source_text.trim()}
                  </Code>
                </Box>
              )
            })}
          </VStack>
        </Box>
      )}

      {/* Detailed Analysis Tabs */}
      <Tabs variant="enclosed">
        <TabList overflowX="auto" whiteSpace="nowrap">
          <Tab fontSize="xs" px={2} py={1} minW="auto">Line Analysis</Tab>
          <Tab fontSize="xs" px={2} py={1} minW="auto">Basic Blocks</Tab>
          <Tab fontSize="xs" px={2} py={1} minW="auto">Unattributed</Tab>
        </TabList>

        <TabPanels>
          {/* Line Analysis */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" wrap="wrap">
                <HStack spacing={2}>
                  <Text fontSize="sm" color={mutedColor}>Sort by:</Text>
                  <Button
                    size="xs"
                    variant={sortBy === 'exclusive' ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => setSortBy('exclusive')}
                  >
                    Exclusive %
                  </Button>
                  <Button
                    size="xs"
                    variant={sortBy === 'inclusive' ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => setSortBy('inclusive')}
                  >
                    Inclusive %
                  </Button>
                  <Button
                    size="xs"
                    variant={sortBy === 'line' ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => setSortBy('line')}
                  >
                    Line #
                  </Button>
                </HStack>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setShowAllLines(!showAllLines)}
                >
                  {showAllLines ? 'Show Significant Only' : 'Show All Lines'}
                </Button>
              </HStack>

              <TableContainer overflowX="auto" maxW="100%" w="100%">
                <Table
                  size="sm"
                  variant="simple"
                  width="auto"
                  sx={{ tableLayout: 'auto', 'th, td': { px: 1, py: 1 } }}
                >
                  <Thead>
                    <Tr>
                      <Th fontSize="xs" w="60px" whiteSpace="nowrap">Line</Th>
                      <Th fontSize="xs" w="15ch" whiteSpace="nowrap">Source Code</Th>
                      <Th fontSize="xs" isNumeric w="120px" whiteSpace="nowrap">
                        <Tooltip label="Exclusive %: Percent of samples directly on this line (self time)" hasArrow placement="top">
                          Exclusive %
                        </Tooltip>
                      </Th>
                      <Th fontSize="xs" isNumeric w="110px" whiteSpace="nowrap">
                        <Tooltip label="Inclusive %: Percent of samples on this line plus time in its inlined/child work" hasArrow placement="top">
                          Incl %
                        </Tooltip>
                      </Th>
                      
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredLines.map((line) => {
                      const scaledExclusive = scalePercentage(line.percentage_exclusive)
                      const scaledInclusive = scalePercentage(line.percentage_inclusive)
                      const maxScaledExclusive = Math.max(...profilerData.lines.map(l => scalePercentage(l.percentage_exclusive)))
                      
                      return (
                        <Tr key={line.line_number}>
                          <Td fontSize="xs" fontWeight="medium">
                            {line.line_number}
                          </Td>
                          <Td fontSize="xs" w="15ch" maxW="15ch" overflow="hidden">
                            <Tooltip label={line.source_text.trim()} hasArrow placement="top-start" fontSize="xs">
                              <Box noOfLines={1} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" maxW="15ch">
                                <Code fontSize="xs" p={0} bg="transparent">
                                  {line.source_text.trim().length > 15 
                                    ? line.source_text.trim().slice(0, 15) + '…' 
                                    : line.source_text.trim()}
                                </Code>
                              </Box>
                            </Tooltip>
                          </Td>
                          <Td fontSize="xs" isNumeric>
                            <HStack justify="flex-end" spacing={0} alignItems="center">
                              <Progress
                                value={scaledExclusive}
                                max={maxScaledExclusive}
                                size="sm"
                                width="28px"
                                colorScheme={getHeatmapColor(scaledExclusive) as any}
                              />
                              <Box w="50px" textAlign="right">
                                <Text color={getHeatmapColor(scaledExclusive)} fontFamily="mono" lineHeight="shorter">
                                  {scaledExclusive.toFixed(2)}%
                                </Text>
                              </Box>
                            </HStack>
                          </Td>
                          <Td fontSize="xs" isNumeric>
                            <Text>{scaledInclusive.toFixed(2)}%</Text>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              {!showAllLines && filteredLines.length < profilerData.lines.length && (
                <Text fontSize="xs" color={mutedColor} textAlign="center">
                  Showing {filteredLines.length} of {profilerData.lines.length} lines 
                  (filtered to significant samples &gt;0.1%)
                </Text>
              )}
            </VStack>
          </TabPanel>

          {/* Basic Blocks */}
          <TabPanel px={0}>
            {profilerData.basic_blocks.length === 0 ? (
              <Text fontSize="sm" color={mutedColor}>
                No samples attributed to basic blocks.
              </Text>
            ) : (
              <TableContainer overflowX="auto" maxW="100%" w="100%">
                <Table size="sm" variant="simple" width="100%" sx={{ tableLayout: 'fixed' }}>
                  <Thead>
                    <Tr>
                      <Th fontSize="xs">Line Range</Th>
                      <Th fontSize="xs" isNumeric w="100px">Samples</Th>
                      <Th fontSize="xs" isNumeric w="120px">Percentage</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {profilerData.basic_blocks
                      .sort((a, b) => scalePercentage(b.percentage) - scalePercentage(a.percentage))
                      .map((block, index) => {
                        const scaledPercent = scalePercentage(block.percentage)
                        
                        return (
                          <Tr key={index}>
                            <Td fontSize="xs">
                              {block.start_line === block.end_line 
                                ? `Line ${block.start_line}`
                                : `Lines ${block.start_line}-${block.end_line}`
                              }
                            </Td>
                            <Td fontSize="xs" isNumeric>{block.samples.toLocaleString()}</Td>
                            <Td fontSize="xs" isNumeric>
                              <Text color={getHeatmapColor(scaledPercent)}>
                                {scaledPercent.toFixed(2)}%
                              </Text>
                            </Td>
                          </Tr>
                        )
                      })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Unattributed (includes Accountability) */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={4}>
              {hasRuntimeData && runtimeRatio < 1 && (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Text fontSize="xs">
                    ℹ️ Percentages in this tab show original unadjusted values including setup overhead, 
                    as these samples could not be attributed to specific source lines.
                  </Text>
                </Alert>
              )}
              {profilerData.unattributed.length > 0 ? (
                <TableContainer overflowX="auto" maxW="100%" w="100%">
                  <Table size="sm" variant="simple" width="100%" sx={{ tableLayout: 'fixed' }}>
                    <Thead>
                      <Tr>
                        <Th fontSize="xs">Symbol</Th>
                        <Th fontSize="xs" isNumeric w="100px">Samples</Th>
                        <Th fontSize="xs" isNumeric w="120px">Percentage</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {profilerData.unattributed
                        .sort((a, b) => b.percentage - a.percentage)
                        .map((sample, index) => (
                        <Tr key={index}>
                          <Td fontSize="xs">
                            <Code fontSize="xs">{sample.symbol}</Code>
                          </Td>
                          <Td fontSize="xs" isNumeric>{sample.samples.toLocaleString()}</Td>
                          <Td fontSize="xs" isNumeric>{sample.percentage.toFixed(2)}%</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Text fontSize="sm" color={mutedColor}>
                  No unattributed samples - all performance data was successfully mapped to source lines.
                </Text>
              )}

              <VStack align="stretch" spacing={4}>
                <HStack spacing={4} wrap="wrap">
                  <Stat size="sm">
                    <StatLabel fontSize="xs">Line Attributed</StatLabel>
                    <StatNumber fontSize="sm">{profilerData.accountability.line_attributed.toLocaleString()}</StatNumber>
                    <StatHelpText fontSize="xs">
                      {((profilerData.accountability.line_attributed / profilerData.accountability.total) * 100).toFixed(1)}%
                    </StatHelpText>
                  </Stat>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">Block Attributed</StatLabel>
                    <StatNumber fontSize="sm">{profilerData.accountability.block_attributed.toLocaleString()}</StatNumber>
                    <StatHelpText fontSize="xs">
                      {((profilerData.accountability.block_attributed / profilerData.accountability.total) * 100).toFixed(1)}%
                    </StatHelpText>
                  </Stat>
                  <Stat size="sm">
                    <StatLabel fontSize="xs">Unattributed</StatLabel>
                    <StatNumber fontSize="sm">{profilerData.accountability.unattributed.toLocaleString()}</StatNumber>
                    <StatHelpText fontSize="xs">
                      {((profilerData.accountability.unattributed / profilerData.accountability.total) * 100).toFixed(1)}%
                    </StatHelpText>
                  </Stat>
                </HStack>
                
                <Box>
                  <Text fontSize="xs" fontWeight="medium" mb={2}>Sample Attribution Breakdown</Text>
                  <Progress
                    value={(profilerData.accountability.line_attributed / profilerData.accountability.total) * 100}
                    colorScheme="green"
                    size="sm"
                    borderRadius="md"
                  />
                  <HStack justify="space-between" mt={1}>
                    <Text fontSize="xs" color="green.500">
                      Line: {profilerData.accountability.line_attributed}
                    </Text>
                    <Text fontSize="xs" color="blue.500">
                      Block: {profilerData.accountability.block_attributed}
                    </Text>
                    <Text fontSize="xs" color="orange.500">
                      Unattributed: {profilerData.accountability.unattributed}
                    </Text>
                  </HStack>
                </Box>
                
                {profilerData.accountability.check !== profilerData.accountability.total && (
                  <Alert status="warning" size="sm">
                    <AlertIcon />
                    <Text fontSize="xs">
                      Accountability check failed: expected {profilerData.accountability.total} but sum is {profilerData.accountability.check}
                    </Text>
                  </Alert>
                )}
              </VStack>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  )
}
